// Real-user Core Web Vitals, sent to GA4 as events (LCP, INP, CLS, FCP, TTFB).
//
// Why: a cancellation survey said the site was "super slow to load on our
// phones", and the only speed data we had was lab runs of the logged-out
// lesson page. This reports what students' own devices experience, on every
// page including the logged-in ones.
//
// web-vitals measures the HARD page load. Turbo visits after it are not new
// measurements, and INP/CLS are only reported when the page is hidden, by
// which time Turbo may have swapped the body. So the page key and audience are
// read ONCE, at install, from the page that actually loaded. Read INP and CLS
// per page_key with that in mind: they cover the whole Turbo session that
// started on that page, including interactions on pages visited after it. A
// back/forward-cache restore reports again with navigation_type
// "back-forward-cache" under the original page_key.
//
// INP and CLS can report more than once per load (every time the tab is
// hidden after the value grew), always with the same metric_id. `value` is
// therefore the DELTA since the previous report, so GA4 sums stay correct;
// metric_value is the running total, for exports that dedupe on metric_id.
//
// Events go to the GA4 property only (send_to). A bare gtag('event', ...)
// reaches every configured target, which includes the Google Ads tag.

export type WebVitalsMetric = {
  readonly name: string;
  readonly value: number;
  readonly rating: string;
  readonly delta: number;
  readonly id: string;
  readonly navigationType?: string;
};

type MetricListener = (callback: (metric: WebVitalsMetric) => void) => void;

export type WebVitalsListeners = {
  readonly onLCP: MetricListener;
  readonly onINP: MetricListener;
  readonly onCLS: MetricListener;
  readonly onFCP: MetricListener;
  readonly onTTFB: MetricListener;
};

type ReporterWindow = {
  isUsingGoogleAnalytics?: boolean;
  googleAnalyticsMeasurementId?: string;
  gtag?: (...args: unknown[]) => void;
  navigator?: { connection?: { effectiveType?: string } };
};

type ReporterDocument = {
  body?: { dataset?: Record<string, string | undefined> } | null;
};

export type PageContext = {
  readonly pageKey: string;
  readonly audience: string;
  readonly effectiveType: string;
};

export function readPageContext(win: ReporterWindow, doc: ReporterDocument): PageContext {
  const dataset = doc.body?.dataset ?? {};
  return {
    pageKey: dataset.webVitalsPage || "unknown",
    audience: dataset.webVitalsAudience || "unknown",
    effectiveType: win.navigator?.connection?.effectiveType || "unknown",
  };
}

// GA4 aggregates `value` as an integer, so CLS (a unitless score, usually
// 0-1) is scaled by 1000; the timings are already milliseconds.
export function webVitalsEventParams(metric: WebVitalsMetric, context: PageContext, measurementId: string) {
  return {
    send_to: measurementId,
    value: Math.round(metric.name === "CLS" ? metric.delta * 1000 : metric.delta),
    metric_value: metric.value,
    metric_delta: metric.delta,
    metric_rating: metric.rating,
    metric_id: metric.id,
    navigation_type: metric.navigationType || "unknown",
    page_key: context.pageKey,
    audience: context.audience,
    effective_type: context.effectiveType,
  };
}

// Returns true when it registered listeners. Does nothing without GA4: no
// measurement id means there is nowhere to send, and never falls back to a
// bare event that the Ads tag would also receive.
export function installWebVitalsReporter(
  listeners: WebVitalsListeners,
  win: ReporterWindow,
  doc: ReporterDocument,
): boolean {
  const measurementId = win.googleAnalyticsMeasurementId;
  if (!win.isUsingGoogleAnalytics || !measurementId) return false;

  const context = readPageContext(win, doc);
  const send = (metric: WebVitalsMetric) => {
    if (typeof win.gtag !== "function") return;
    win.gtag("event", metric.name, webVitalsEventParams(metric, context, measurementId));
  };

  listeners.onLCP(send);
  listeners.onINP(send);
  listeners.onCLS(send);
  listeners.onFCP(send);
  listeners.onTTFB(send);
  return true;
}
